/**
 * AutoFin integration utilities
 * Handles building iframe URLs for the AutoFin Araña credit evaluation system
 */

import axios from 'axios'

import type { AutoFinLookupResult } from './autofinMapping'

// Get API URL from environment or default to relative path
// VITE_API_URL typically ends with /api/ in this project (e.g. https://api2.veekls.com/api/)
const API_URL = import.meta.env.VITE_API_URL || '/api/'

export interface AutoFinConfig {
  baseUrl: string
  codSpider: string
}

export interface AutoFinVehicleData {
  brand: number
  model: number
  year: number
  price: number
  status: 1 | 2 // 1 = Nuevo, 2 = Usado
  isMoto?: boolean
  vehicleType?: number
}

export interface AutoFinClientData {
  firstName?: string
  lastName?: string
  rut?: string
  phone?: string
  email?: string
}

export interface AutoFinCreditParams {
  businessType: 1 | 2 // 1 = Autorenuevate, 2 = Convencional
  idExterno?: string
}

/**
 * Maps internal credit type to AutoFin business type
 * Internal types: 0 = Convencional, 1 = Compra inteligente
 * AutoFin business types: 2 = Convencional, 1 = Autorenuevate
 * @param creditType - Internal credit type (0 or 1)
 * @returns AutoFin business type (1 or 2)
 */
export const mapCreditTypeToBusinessType = (creditType: number | undefined): 1 | 2 => {
  // Map: type 1 (Compra inteligente) → businessType 1 (Autorenuevate)
  //       type 0 (Convencional) → businessType 2 (Convencional)
  return creditType === 1 ? 1 : 2
}

/**
 * Builds AutoFin iframe URL for credit evaluation
 */
export const buildAutoFinUrl = (
  config: AutoFinConfig,
  vehicle: AutoFinVehicleData,
  client?: AutoFinClientData,
  creditParams?: AutoFinCreditParams
): string => {
  const params = new URLSearchParams({
    businessType: creditParams?.businessType?.toString() || '2',
    vehicleStatus: vehicle.status.toString(),
    brand: vehicle.brand.toString(),
    model: vehicle.model.toString(),
    year: vehicle.year.toString(),
    price: vehicle.price.toString(),
  })

  // Add motorcycle-specific parameters
  if (vehicle.isMoto) {
    params.append('isMoto', 'true')

    if (vehicle.vehicleType) {
      params.append('vehicleType', vehicle.vehicleType.toString())
    }
  }

  // Add optional client data
  if (client?.firstName) params.append('firstName', client.firstName)

  if (client?.lastName) params.append('lastName', client.lastName)

  if (client?.rut) params.append('rut', client.rut || '')

  if (client?.phone) params.append('phone', client.phone || '')

  if (client?.email) params.append('email', client.email || '')

  // Add external ID if provided
  if (creditParams?.idExterno) {
    params.append('idExterno', creditParams.idExterno)
  }

  return `${config.baseUrl}/${config.codSpider}?${params.toString()}`
}

/**
 * Configuration constants for AutoFin integration
 */
export const AUTOFIN_CONFIG = {
  MIN_VEHICLE_PRICE: 700000, // Minimum vehicle price for successful evaluation
  IVA_RATE: 1.19, // IVA rate (19%)
  DEFAULT_BRAND_CODE: 107, // Default test brand code
  DEFAULT_MODEL_CODE: 3691, // Default test model code
}

/**
 * Default configuration for AutoFin integration
 */
export const defaultAutoFinConfig: AutoFinConfig = {
  baseUrl: 'https://spider.autofin.cl', // Production URL (confirmed)
  codSpider: 'C772S77204', // Automotora code (confirmed)
}

/**
 * Maps vehicle data from the app to AutoFin format
 * Now uses automatic lookup API to find correct brand/model codes
 */
export const mapVehicleToAutoFin = async (
  vehicle: any,
  dealId?: string
): Promise<AutoFinVehicleData & { _matchConfidence?: string; _matchedBrand?: string; _matchedModel?: string }> => {
  let brandCode: number | null = null
  let modelCode: number | null = null
  let matchConfidence = 'none'
  let matchedBrand = ''
  let matchedModel = ''

  // Step 1: Check for manual overrides (highest priority)
  if (
    vehicle.brandAutofin &&
    vehicle.modelAutofin &&
    typeof vehicle.brandAutofin === 'number' &&
    typeof vehicle.modelAutofin === 'number'
  ) {
    console.log('✅ Using manual AutoFin code overrides', {
      brandCode: vehicle.brandAutofin,
      modelCode: vehicle.modelAutofin,
    })

    brandCode = vehicle.brandAutofin
    modelCode = vehicle.modelAutofin
    matchConfidence = 'manual'
    matchedBrand = vehicle.brand
    matchedModel = vehicle.model
  }

  // Step 2: Try automatic lookup if no override
  else if (vehicle.brand && vehicle.model) {
    try {
      console.log('🔍 Looking up AutoFin codes for:', {
        brand: vehicle.brand,
        model: vehicle.model,
      })

      const response = await axios.post<AutoFinLookupResult>(`${API_URL}autofin-mapping/lookup`, {
        brand: vehicle.brand,
        model: vehicle.model,
      })

      if (response.data.brandId && response.data.modelId) {
        brandCode = response.data.brandId
        modelCode = response.data.modelId
        matchConfidence = response.data.confidence
        matchedBrand = response.data.matchedBrand || ''
        matchedModel = response.data.matchedModel || ''

        console.log('✅ AutoFin mapping found:', {
          original: `${vehicle.brand} ${vehicle.model}`,
          matched: `${matchedBrand} ${matchedModel}`,
          codes: `Brand: ${brandCode}, Model: ${modelCode}`,
          confidence: matchConfidence,
          confidenceScore: response.data.confidenceScore,
        })

        // Persist the mapped IDs to the deal document if dealId is provided
        if (dealId) {
          try {
            await axios.patch(`${API_URL}autofin-webhook/update-deal-mapping`, {
              dealId,
              autofinBrandId: brandCode,
              autofinModelId: modelCode,
            })
            console.log('💾 Successfully persisted AutoFin mapping to deal:', { dealId, brandCode, modelCode })
          } catch (patchError) {
            console.error('❌ Failed to persist AutoFin mapping to deal:', {
              dealId,
              brandCode,
              modelCode,
              error: patchError,
            })
          }
        }
      } else {
        console.warn('⚠️ No AutoFin mapping found for:', { brand: vehicle.brand, model: vehicle.model })
      }
    } catch (error) {
      console.error('❌ Failed to lookup AutoFin codes:', error)
    }
  }

  // Check if we have valid codes
  if (!brandCode || !modelCode) {
    throw new Error(
      `No se pudo encontrar una equivalencia en AutoFin para el vehículo: ${vehicle.brand} ${vehicle.model}. Por favor contacte a soporte.`
    )
  }

  // Step 3: Return mapped data with metadata
  return {
    brand: brandCode,
    model: modelCode,
    year: vehicle.year || new Date().getFullYear(),
    price: Math.round(Math.max(vehicle.price || 0, AUTOFIN_CONFIG.MIN_VEHICLE_PRICE)),
    status: vehicle.status === 'Nuevo' ? 1 : 2,
    isMoto: vehicle.isMoto || false,
    vehicleType: vehicle.vehicleType,
    _matchConfidence: matchConfidence,
    _matchedBrand: matchedBrand,
    _matchedModel: matchedModel,
  }
}

/**
 * Maps client data from the app to AutoFin format
 */
export const mapClientToAutoFin = (client: any): AutoFinClientData => {
  return {
    firstName: client.firstname || client.firstName,
    lastName: client.lastname || client.lastName,
    rut: client.rut || client.rut,
    phone: client.phone?.replace(/\D/g, ''), // Remove non-digits
    email: client.email,
  }
}
